import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback } from 'react';

import { useApiClient } from '@@common';

import {
	AddPropertyV2Model,
	DataSchemaClient,
	EnumPropertyModel,
	NewEntityAsNewPropertyRequest,
	NewEntitySchemaRequest,
	PrimitiveType,
	PropertyTypes,
	SchemaTypes,
	SimplePropertyModel,
	UpdatePropertyModel,
} from '../../../api/api';

type UseEntityControllerOptions = {
	enabled?: boolean;
};

const AllPrimitiveTypes: PrimitiveType[] = [
	PrimitiveType.String,
	PrimitiveType.Integer,
	PrimitiveType.Boolean,
	PrimitiveType.Date,
	PrimitiveType.Time,
	PrimitiveType.DateTime,
	PrimitiveType.Decimal,
	PrimitiveType.Guid,
];

export const useEntityController = (contextId: string, options: UseEntityControllerOptions = {}) => {
	const client = useApiClient(DataSchemaClient);
	const isEntitiesQueryEnabled = options.enabled ?? true;

	// Fetch entities with React Query - automatic caching
	const {
		data: entities = [],
		isLoading: loadingEntities,
		refetch,
	} = useQuery({
		queryKey: ['entities', contextId],
		queryFn: () => client.getEntitiesForContext(contextId, undefined, true),
		enabled: !!contextId && isEntitiesQueryEnabled,
		staleTime: 60000, // Cache for 1 minute (entities change less frequently)
		gcTime: 5 * 60 * 1000,
	});

	const mutations = useEntityMutations(contextId);

	return {
		entities,
		refreshEntities: async () => {
			await refetch();
		},
		loadingEntities,
		...mutations,
	};
};

export const useEntityMutations = (contextId: string) => {
	const client = useApiClient(DataSchemaClient);
	const queryClient = useQueryClient();

	const createRootEntity = useCallback(
		async (name: string, description: string) => {
			const newEntity: NewEntitySchemaRequest = {
				entityName: name,
				description: description,
				schemaType: SchemaTypes.RootDataSchema,
				properties: [],
				enumProperties: [],
				referenceProperties: [],
			};
			await client.newEntity(contextId!, newEntity);
			// Invalidate entities cache to trigger refetch
			queryClient.invalidateQueries({ queryKey: ['entities', contextId] });
			queryClient.invalidateQueries({ queryKey: ['domainModel', contextId] });
		},
		[client, contextId, queryClient],
	);

	const getEntityById = useCallback(
		async (entityId: string) => {
			const entity = await client.getEntity(contextId!, entityId);
			if (!entity) return null;
			return entity;
		},
		[client, contextId],
	);

	const getEntitySummaryById = useCallback(
		async (entityId: string) => {
			const entity = await client.getEntitySummary(contextId!, entityId, false);
			if (!entity) return null;
			return entity;
		},
		[client, contextId],
	);

	const addProperty = useCallback(
		async (entityId: string, newProperty: SimplePropertyModel) => {
			await client.addProperty(contextId!, entityId, newProperty);
			console.log('Adding property with name:', newProperty?.name, 'to entity with ID:', entityId);
		},
		[client, contextId],
	);

	const updateEntityDescription = useCallback(
		async (entityId: string, newDescription: string) => {
			await client.updateDescription(contextId!, entityId, { description: newDescription });
			console.log('Updating description for entity with ID:', entityId);
		},
		[client, contextId],
	);

	const addSchemaProperty = useCallback(
		async (entityId: string, newProperty: NewEntityAsNewPropertyRequest) => {
			await client.addSchemaProperty(contextId!, entityId, newProperty);
			console.log('Adding property with name:', newProperty?.propertyName, 'to entity with ID:', entityId);
		},
		[client, contextId],
	);

	const addReferenceProperty = useCallback(
		async (entityId: string, newProperty: AddPropertyV2Model) => {
			await client.addPropertyV2(contextId!, entityId, newProperty);
			console.log('Adding reference property with name:', newProperty?.name, 'to entity with ID:', entityId);
		},
		[client, contextId],
	);

	const addEnumProperty = useCallback(
		async (entityId: string, newProperty: EnumPropertyModel) => {
			await client.addEnumProperty(contextId!, entityId, newProperty);
			console.log('Adding enum with name:', newProperty?.propertyName, 'to entity with ID:', entityId);
		},
		[client, contextId],
	);

	const deleteProperty = useCallback(
		async (entityId: string, propertyName: string) => {
			await client.removeProperty(contextId, entityId, propertyName);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['entities', contextId] }),
				queryClient.invalidateQueries({ queryKey: ['domainModel', contextId] }),
			]);
		},
		[client, contextId, queryClient],
	);

	const updateProperty = useCallback(
		async (entityId: string, propertyName: string, propertyType: PropertyTypes, updatedProperty: UpdatePropertyModel) => {
			try {
				await client.updateProperty(contextId ?? '', entityId ?? '', propertyName, propertyType, updatedProperty);
				// Optional: trigger a refresh or notify success
			} catch (error) {
				console.error('Failed to update property:', error);
				// Optional: show error to user
			}
		},
		[client, contextId],
	);

	return {
		propertyTypes: AllPrimitiveTypes,
		createRootEntity,
		addProperty,
		deleteProperty,
		getEntityById,
		getEntitySummaryById,
		addSchemaProperty,
		addReferenceProperty,
		addEnumProperty,
		updateProperty,
		updateEntityDescription,
	};
};
